/**
 * Operations Event Schemas — the platform-wide operational timeline.
 *
 * One append-only spine for everything the platform does to ITSELF: the app
 * fleet (builds, OTA updates, submissions, release outcomes, configuration
 * edits, assets, onboarding, push wiring), publishing accounts (enrollment,
 * ownership, refusals, agreement checks, credentials), agent runs,
 * integrations, and infrastructure plumbing.
 *
 * This is deliberately NOT `SystemEventLog`. That spine is school- and
 * user-scoped, school-admin-visible, and indexed for school-scoped reads;
 * this one is operator-scoped and gated on `isSuperAdmin` at every reader.
 * Two spines, split on tenancy — see KD2 in the plan.
 *
 * Every vocabulary below is a CLOSED Zod union, and none of them is a
 * database enum (KD8). Closed because the operator UI groups and filters by
 * these strings without matching on substrings, and a typo in a call site
 * should be a compile error rather than an orphan row nothing will query.
 * Not a DB enum because growing a catalog must never need a PlanetScale
 * promotion — adding an event type costs one string and no DDL.
 *
 * These strings are the API. A rename after rows exist is a data migration,
 * not an edit, which is why they land before anything that writes them.
 *
 * Plan: docs/_archive/2026-10-17-plans-sweep/plans/2026-08-23-0114-feat-operations-event-spine-plan.md
 */

import { z } from 'zod'
import {
  StoreRejectionRemedyRunEndStatusSchema,
  StoreRejectionStoreSchema,
  StoreRejectionTemplateIdSchema
} from './store-rejection'

// =============================================================================
// Domain
// =============================================================================

/**
 * The top-level slice of the platform an event belongs to.
 *
 * Four values, chosen so that an operator can scope the whole timeline to the
 * area they are on call for without knowing any event type:
 *
 * - `app_fleet`      mobile apps, publishing accounts, releases, builds,
 *                    credentials, assets
 * - `agent`          agent runs, observatory findings, AI worker jobs
 * - `integration`    connectors, MCP clients, issued tokens, third-party links
 * - `infrastructure` scheduled tasks, workers, queues, deploys, and
 *                    subject-less webhook plumbing
 */
export const OPERATIONS_EVENT_DOMAINS = ['app_fleet', 'agent', 'integration', 'infrastructure'] as const

export const OperationsEventDomainSchema = z.enum(OPERATIONS_EVENT_DOMAINS)
export type OperationsEventDomain = z.infer<typeof OperationsEventDomainSchema>

// =============================================================================
// Entity type (the event's subject)
// =============================================================================

/**
 * What an event is ABOUT. Three values, and that is the whole v1 list.
 *
 * An entity type earns a value only when BOTH hold: something writes events
 * against it, and an operator browses its timeline. A value nothing writes is
 * a value the next implementer has to guess about; a subject nobody browses is
 * a rollup key wearing the wrong hat.
 *
 * Six candidates were considered and deliberately excluded — `app_build`,
 * `app_deployment`, `app_version`, `app_release_target`,
 * `provisioning_credential`, `publishing_account_standing`, `asset_slot` —
 * because every event that would have addressed them is in fact written
 * against the app or the account today. Adding one back later costs one string
 * and no promotion (KD1), which is what makes starting narrow safe rather than
 * short-sighted.
 *
 * A NULL subject is legal on the row and means the event is about the platform
 * itself (subject-less webhook plumbing, for instance) — hence the nullable
 * field on the wire schema below rather than a fourth `platform` member here.
 */
export const OPERATIONS_EVENT_ENTITY_TYPES = ['mobile_app', 'publishing_account', 'app_release'] as const

export const OperationsEventEntityTypeSchema = z.enum(OPERATIONS_EVENT_ENTITY_TYPES)
export type OperationsEventEntityType = z.infer<typeof OperationsEventEntityTypeSchema>

// =============================================================================
// Actor
// =============================================================================

/**
 * Who or what caused the event.
 *
 * Widened from the four `MOBILE_APP_EVENT_ACTOR_TYPES` by one: `scheduler`
 * separates "a cron fired this" from "a process decided this", which is the
 * distinction you need at 3am to know whether anyone chose anything.
 *
 * `agent` stays distinct from `human` because R23 requires agent-initiated
 * actions to be attributable as such everywhere they surface. None of these
 * values is ever supplied by a caller: the recorder takes a branded actor
 * produced by one of four server-side constructors, so nothing read from an
 * HTTP body can reach an actor column.
 */
export const OPERATIONS_EVENT_ACTOR_TYPES = ['human', 'agent', 'webhook', 'system', 'scheduler'] as const

export const OperationsEventActorTypeSchema = z.enum(OPERATIONS_EVENT_ACTOR_TYPES)
export type OperationsEventActorType = z.infer<typeof OperationsEventActorTypeSchema>

// =============================================================================
// Event classes
// =============================================================================

/**
 * The coarse grouping the timeline UI filters on.
 *
 * The five `MOBILE_APP_EVENT_CLASSES` carry forward unchanged, plus three the
 * fleet catalog never needed: `lifecycle` (an entity coming into existence or
 * changing state), `credential` (key material moving), and `compliance`
 * (refusals and agreement checks — the class an auditor asks for by name).
 */
export const OPERATIONS_EVENT_CLASSES = [
  'lifecycle',
  'delivery',
  'configuration',
  'credential',
  'compliance',
  'asset',
  'onboarding',
  'push',
  'agent_run'
] as const

export const OperationsEventClassSchema = z.enum(OPERATIONS_EVENT_CLASSES)
export type OperationsEventClass = z.infer<typeof OperationsEventClassSchema>

// =============================================================================
// Severity
// =============================================================================

export const OPERATIONS_EVENT_SEVERITIES = ['info', 'warn', 'error'] as const

export const OperationsEventSeveritySchema = z.enum(OPERATIONS_EVENT_SEVERITIES)
export type OperationsEventSeverity = z.infer<typeof OperationsEventSeveritySchema>

// =============================================================================
// Event-type catalog (closed union, grouped by class)
// =============================================================================

/**
 * Every recordable event type, mapped to the class it belongs to.
 *
 * Naming: `<subject>.<past-tense outcome>`. The outcome lives in the type
 * (`build.succeeded` vs `build.failed`) rather than in a separate column, so
 * "what failed for this app" is a prefix scan over one field.
 *
 * 42 of the entries below are `MOBILE_APP_EVENT_TYPES` carried forward with
 * their names AND their classes unchanged — that catalog's rows get backfilled
 * onto this table, so a renamed type would orphan history. They are not a
 * contiguous block: a later addition belongs beside the family it joins, not
 * appended to the end where nothing reading the file would find it. They are spelled out
 * here rather than spread in from `mobile-app-event.ts` because that catalog
 * is being frozen and will eventually be deleted; this one has to stand on its
 * own. A test asserts the two agree entry-for-entry, so the drift that costs
 * is a red test rather than a silent divergence.
 */
export const OPERATIONS_EVENT_TYPES = {
  // --- delivery: builds, submissions, OTA updates, releases ---
  // Release-level types (`release.created`, `.targets_added`, `.cancelled`,
  // `.completed`, `.failed`, `.status_changed`) take `entityType: app_release`;
  // every `build.*`, `ota.*`, `submission.*`, and `release.target_*` takes
  // `entityType: mobile_app` and carries `appReleaseId` as a rollup where a
  // release drove it. That is the line the shipped code already draws, and it
  // gives both readings — the app timeline and the release page — off a single
  // write.
  'release.created': 'delivery',
  'release.targets_added': 'delivery',
  'release.cancelled': 'delivery',
  'release.completed': 'delivery',
  /**
   * A release reached the terminal FAILED state.
   *
   * Its own type rather than `release.completed` at `severity: 'error'`. The
   * catalog names past-tense OUTCOMES, and a failure filed under "completed"
   * makes the plainest query the table exists to answer — "every release
   * completion ever" — count failures as successes. Severity is a modifier on
   * an outcome, never a substitute for one.
   */
  'release.failed': 'delivery',
  /**
   * A release moved between NON-terminal states (DRAFT, IN_PROGRESS).
   *
   * NOT `release.target_dispatched`. That is a TARGET-level type, and the
   * subject here is the release aggregate: `updateAppReleaseStatus` recomputes
   * the aggregate from its targets and records the aggregate's transition, so
   * filing it under a target type attributes a release-level fact to a target
   * that may not have moved at all — and makes a target timeline show
   * dispatches that never happened.
   */
  'release.status_changed': 'delivery',
  'release.target_dispatched': 'delivery',
  'release.target_retried': 'delivery',
  'release.target_failed': 'delivery',
  'release.target_cancelled': 'delivery',
  /**
   * The per-outcome target vocabulary (SCH-1297, KTD8).
   *
   * `transitionTarget` used to file EVERY target move — started, advanced,
   * failed, blocked, released, refused — as `release.target_dispatched`: 117 of
   * the last 125 rows on the release page were that one string, which is why
   * the Events tab read as a wall of identical badges. Each outcome now names
   * itself, so "which targets went live" and "which were refused" are queries
   * rather than a payload scan.
   *
   * `release.target_dispatched` is NOT retired — historical rows carry it, and
   * the reader still has to render them.
   */
  'release.target_started': 'delivery',
  'release.target_advanced': 'delivery',
  'release.target_blocked': 'delivery',
  /**
   * The platform declined to act on a transition someone asked for: an event
   * that arrived after a terminal outcome, an illegal edge, or a guarded claim
   * whose row had already moved.
   *
   * Its own type rather than a severity, for the reason `release.failed` is not
   * `release.completed` at `severity: 'error'` — "what did we refuse" is the
   * question, and burying it under an outcome that says the opposite makes it
   * unanswerable.
   */
  'release.target_refused': 'delivery',
  'release.target_released': 'delivery',
  /**
   * A provider webhook was rejected before it could bind to anything: a payload
   * that failed validation, or one whose platform disagreed with the artifact it
   * named.
   *
   * Recorded because the alternative is silence. A GOOGLE_PLAY submission bound
   * to an IOS target in production (2026-09-03) precisely because a `buildId`-less
   * body dropped its where-clause and matched the newest EAS artifact of any
   * kind; nothing anywhere said a webhook had been mis-bound.
   */
  'release.webhook_refused': 'delivery',
  /**
   * The registry's values pushed into an app's EAS environments, which is what every later build
   * of that app reads. Recorded where the write resolves: this route calls Expo directly, so
   * unlike a build dispatch there is no callback later to instrument.
   *
   * `configuration`, not `delivery`: nothing ships here. It is an act on the app's build wiring,
   * the same reading that puts `webhook.registered` in this class.
   */
  'eas_env.populated': 'configuration',
  /**
   * An App Store Connect API key attached to an app's EAS iOS credentials, which is the
   * credential every later `eas submit` for that app authenticates to Apple with.
   *
   * `credential`, not `configuration`: the sibling `eas_env.populated` changes values a build
   * reads, while this changes WHICH KEY acts on Apple's side on our behalf — the same reading
   * that puts `impersonation.actor_token_created` in this class. EAS keeps no account of who
   * changed a binding, so this entry is the only one.
   */
  'submit_key.bound': 'credential',
  'build.dispatched': 'delivery',
  'build.started': 'delivery',
  'build.succeeded': 'delivery',
  'build.failed': 'delivery',
  /**
   * A CI run snapshotted a school's registry record into the generated bundle
   * it is about to build or publish from (plan 2026-09-01-2310 KTD8, R20).
   *
   * Keyed on the workflow RUN id rather than on a release target, because a run
   * dispatched from the GitHub UI has no target and therefore no artifact to
   * carry its provenance — and a build whose provenance is unrecoverable is
   * exactly what R20 exists to prevent. When a target does exist the same
   * manifest also lands on `AppArtifact.payload`; this row is the floor, not a
   * duplicate of it.
   */
  'build.snapshot_recorded': 'delivery',
  /**
   * The Appetize upload STARTED — the artifact was accepted for publishing and
   * the platform is now waiting on Appetize.
   *
   * Recorded because the upload is not instantaneous: Appetize downloads the
   * artifact inside its own request, so a target sits in DELIVERING for tens of
   * seconds. Without a start row that window is a gap in the timeline, and the
   * only two rows are "asked for" (`build.succeeded`) and "over" — which is
   * exactly the shape that makes a wedged publish look like one that was never
   * attempted.
   */
  'appetize.publish_started': 'delivery',
  /**
   * A simulator build was pushed to Appetize so it is playable in a browser.
   *
   * `delivery`, not `build`: the build already succeeded and was recorded as
   * `build.succeeded`; this is what happened to the artifact AFTERWARDS, which
   * is the same relationship `submission.submitted` has to a store binary.
   * Appetize is simply the delivery lane a variant no store will accept takes.
   */
  'appetize.published': 'delivery',
  /**
   * The Appetize upload was attempted and did not succeed — no API key, an
   * artifact Appetize refused, or a network failure.
   *
   * Its own type rather than a severity on `appetize.published`, for the reason
   * `submission.failed` is separate from `submission.rejected`: "how many
   * simulator builds are actually playable" must not be answered by counting
   * attempts.
   */
  'appetize.failed': 'delivery',
  /**
   * The publish was deliberately NOT attempted — no API key is configured, or
   * another delivery of the same build already claimed the upload.
   *
   * Separate from `appetize.failed` because a skip is not a failure: writing a
   * `warn` failure row for "the platform correctly did nothing" trains an
   * operator to ignore the one row that means something went wrong. A skip is
   * `info` and says why; a failure is `warn` and says what broke.
   */
  'appetize.skipped': 'delivery',
  'submission.submitted': 'delivery',
  'submission.review_started': 'delivery',
  'submission.approved': 'delivery',
  'submission.rejected': 'delivery',
  'submission.released': 'delivery',
  /**
   * An operator (or an agent acting for one) asked Apple to release an approved
   * version — our `POST /v1/appStoreVersionReleaseRequests` call, or the typed
   * refusal that stopped it before it was made (SCH-1319 R10, KTD5).
   *
   * Deliberately NOT folded into `submission.released`. They are two different
   * facts about two different actors: this one is US asking, written by the
   * release action; `submission.released` is APPLE confirming, written by the
   * ASC webhook lane. Folding them would double-count every release — one row
   * for the request and one for the store's own confirmation of the same
   * publish — and a second request for a version already requested is permitted
   * (R14), so the request side is not even one-per-release.
   */
  'submission.release_requested': 'delivery',
  /**
   * A live probe of what Apple says about an app's current App Store version —
   * the refresh route, and every one of its outcomes (SCH-1319 R10, KTD2).
   *
   * RECORDED ON EVERY ATTEMPT, including the ones that never reached a
   * determination. The probe writes no column and changes nothing, so without
   * this row an operator who re-checks and sees nothing move cannot tell a
   * failure from a no-op — the same reason `account.api_access_checked` is
   * written per attempt rather than per state change.
   *
   * `delivery`, not `configuration`: it is a provider reporting where a
   * submission stands, which is the evidence class `submission.*` occupies.
   * Severity carries the difference between a determination (`info`) and an
   * unreadable probe (`warn`); the class does not.
   */
  'submission.version_checked': 'delivery',
  /**
   * A store submission reached a TERMINAL failure — Expo reported the
   * submission `errored` or `canceled`.
   *
   * Its own type rather than `submission.rejected`, which means the store
   * reviewed the build and said no. This one never reached review: the upload
   * or the transfer failed. Filing them together would make "how often does
   * Apple reject us" count our own broken submissions as rejections.
   *
   * Deliberately NOT recorded for `in-queue` / `in-progress`: those are in
   * flight, and the catalog names past-tense OUTCOMES.
   */
  'submission.failed': 'delivery',
  /**
   * A Linear ticket was raised for a recorded rejection, and the operator's fix
   * was recorded against it.
   *
   * The second and third halves of one rejection: `submission.rejected` is the
   * store saying no, this pair is the work that answered it. All three share a
   * `correlationId`, so the cause can be classified days after the rejection
   * without mutating an appended row or counting the rejection twice (KTD1).
   *
   * `delivery`, not `configuration` where the rest of the `app.*` family sits.
   * The class is the timeline filter, and an operator asking "what happened to
   * this submission" has to see the store's no, the ticket, and the fix in one
   * place; splitting the arc across two filters would make the ticket and the
   * remedy read as unrelated events.
   *
   * `app.rejection_remedied` carries the cause class and the remedy — including
   * a remedy no store reported, which is the brand-rights letter and the reason
   * detection cannot produce this event by itself (R13). Its absence is what
   * makes a rejection read as outstanding (R14, R24): the outstanding bucket is
   * derived from rejections with no remedied event, never stored as a cause.
   * Cause-class vocabulary: `store-rejection.ts`.
   */
  'app.rejection_ticket_filed': 'delivery',
  'app.rejection_remedied': 'delivery',
  /**
   * A remedy run started from the Rejections tab, and how it ended (SCH-2889).
   * Runs are not stored in a table: these two events, joined by `runId` in the
   * payload, ARE the run record, and the setup drive or prompt job named by
   * `runId` carries its live progress. Both events take the rejection's
   * `correlationId` (the one the operator clicked), so they sit in the same
   * `delivery` arc as the rejection, its ticket and its remedy.
   *
   * Neither carries reviewer text (R8). `app.rejection_remedied` is still
   * written once, when a run is observed resubmitted (KTD9).
   * Payloads: `RejectionRemedyStartedPayloadSchema` and
   * `RejectionRemedyEndedPayloadSchema` below.
   */
  'app.rejection_remedy_started': 'delivery',
  'app.rejection_remedy_ended': 'delivery',
  /**
   * TestFlight external-build state, from `buildBetaDetailExternalBuildStateUpdated`.
   *
   * Two types, not one per Apple state: `testflight.ready_for_testing` is the
   * transition anyone actually waits on (the build reached external testers),
   * and everything else on that lane — beta review submitted, approved,
   * rejected, expired — lands on `testflight.state_changed` with Apple's own
   * state in the payload. Same reasoning as `delivery.apple_event_unhandled`:
   * Apple's enum is theirs to grow, and a member per state would make their
   * addition our shared-types edit.
   */
  'testflight.ready_for_testing': 'delivery',
  'testflight.state_changed': 'delivery',
  'ota.dispatched': 'delivery',
  'ota.published': 'delivery',
  'ota.failed': 'delivery',
  /**
   * An App Store Connect webhook event this platform has no processor for.
   *
   * Deliberately ONE type covering every unhandled Apple event rather than one
   * per Apple event type: Apple's catalog is theirs to grow, and minting a
   * union member per addition would make "Apple shipped a new event" a
   * shared-types edit — which is exactly the coupling recording these exists to
   * avoid. Apple's real `eventType` rides in the summary and payload.
   */
  'delivery.apple_event_unhandled': 'delivery',

  /**
   * VERSION LEDGER (plan 2026-09-02-2140). Seven types, one per way a
   * `MobileAppDeliveryState` fact group moves.
   *
   * The three `*_observed` types are `delivery`: a provider proved what was
   * built, published or served, which is exactly the evidence class a build or a
   * submission writes. They carry `dedupeOn: [platform, group, value]`, so a
   * sweep that observes an unchanged version records NOTHING — at a 6-hour
   * cadence over 100 apps, an event per tick would be 1,200 rows a day saying
   * the fleet did not move.
   */
  'delivery_state.build_observed': 'delivery',
  'delivery_state.ota_observed': 'delivery',
  'delivery_state.store_live_observed': 'delivery',
  /**
   * A person (or an agent through the internal endpoint) set or cleared a fact
   * group by hand. `configuration`, alongside `app.store_state_changed` and for
   * the same reason: a hand-typed version is a DECLARATION, and mixing it into
   * the delivery stream would make that stream unreliable as evidence of what a
   * provider actually reported.
   *
   * Never deduplicated: a re-submitted identical value is already a full no-op
   * at the writer (R11), so every event that reaches the recorder describes a
   * real move and must be recorded even when it restores a previous value.
   */
  'delivery_state.set_manually': 'configuration',
  'delivery_state.cleared_manually': 'configuration',
  /**
   * An observation displaced a manual value (R12). `delivery`, not
   * `configuration`: the actor is the sweep and the evidence is the provider's:
   * this is the record of the machine overruling the human, and the payload
   * names the displaced value and who had set it so the human can see why.
   */
  'delivery_state.manual_corrected': 'delivery',
  /**
   * One row per completed sweep, carrying the flagged app names and counts
   * (R22), `dedupeOn: [runDate, lanes]`. It exists so a flag that persists for
   * weeks is discoverable without scanning fleet-table cells.
   */
  'delivery_state.sweep_completed': 'delivery',
  /**
   * A super-admin pressed "Verify now" on the per-app page (R19) and a
   * single-app reconcile was queued. Recorded because the request stamps
   * `reconcileQueuedAt` on both platform rows — a write with no other history
   * entry — and because who asked for a re-check is part of the version story.
   * Not emitted for an `already_queued` answer: nothing changed.
   */
  'delivery_state.reconcile_requested': 'delivery',

  // --- configuration: registry edits and credentials ---
  'app.created': 'configuration',
  'app.updated': 'configuration',
  /**
   * A super-admin (or the internal principal) moved ONE store's listing state —
   * `iosStoreState` or `androidStoreState` (plan 2026-09-03-0330 R3).
   *
   * `configuration`, alongside `app.updated`, rather than `delivery`: this
   * records a person's DECLARATION about where the app stands, not an observed
   * delivery transition. The `delivery` class is what a build, a submission or a
   * store webhook writes, and mixing a hand-typed claim into that stream would
   * make the delivery timeline unreliable as evidence.
   *
   * One event per store per move, never a combined one — the payload carries
   * `{ platform, from, to, source, note }`, and a no-op write (same state, same
   * note) records nothing at all.
   */
  'app.store_state_changed': 'configuration',
  /**
   * The platform registered (or re-pointed) this app's App Store Connect webhook
   * (SCH-1294 R4). `entityType: mobile_app`. Payload `{ webhookId, url, reused }`,
   * never the secret. Readiness reads the latest one as "registered".
   */
  'webhook.registered': 'configuration',
  /**
   * A test ping was matched back to a `webhookPingCreated` delivery for this app
   * (SCH-1294 R6). `entityType: mobile_app`. Recorded only on a match, so its
   * presence is proof Apple reached us.
   */
  'webhook.ping_received': 'configuration',
  'app.credentials_updated': 'configuration',
  /**
   * A short-lived scoped token was minted from a vaulted provisioning credential
   * for an external consumer (credential vault R8/KTD7).
   *
   * Recorded only when the request named an app; an account-scoped issuance has
   * no app dimension and is recorded as a stable log line instead. The payload
   * carries the caller identifier, the account, the key id and the scope — never
   * the token and never key material.
   */
  'app.credentials_issued': 'configuration',
  /**
   * An issuance was REFUSED — the account row is suspended (`isActive: false`),
   * which is the only revocation Apple allows short of destroying the key.
   *
   * A separate type rather than a flag on `app.credentials_issued`: the catalog
   * names past-tense outcomes, and recording a refusal under "issued" makes the
   * one query an incident asks — "was a token ever handed out for this account"
   * — answer wrongly.
   */
  'app.credentials_issuance_refused': 'configuration',
  'app.onboarding_config_updated': 'configuration',
  'app.pre_login_settings_updated': 'configuration',
  'app.branding_config_updated': 'configuration',
  /**
   * One store's listing copy changed (school app design plan KTD14). Mirrors
   * `app.branding_config_updated`: the payload names the changed fields, never
   * their values, plus the store, the locale and the lane the write came through
   * (`StoreListingUpdatedEventPayloadSchema` in `mobile-app-store-listing.ts`).
   *
   * The ONLY attribution a listing write has. Unlike onboarding copy there is no
   * version table behind it, so this row is the whole answer to "who changed the
   * App Store description". A write that changes nothing records nothing.
   *
   * Operations catalog only, like `app.demo_login_changed`: the mobile-app
   * catalog is frozen.
   */
  'app.store_listing_updated': 'configuration',
  /**
   * An operator set or cleared the prerequisite holding one app back on one
   * release line and platform (SCH-2198 R5). `MobileAppReleaseLine.blockedReason`
   * keeps only the current text, so this row is the only record of who held the
   * app back, when, and what the reason said before it changed.
   *
   * Operations catalog only, like `app.store_listing_updated`: the mobile-app
   * catalog is frozen.
   */
  'app.release_line_blocked_reason_updated': 'configuration',
  /**
   * A school saved a draft of a field baked into its app's binary: the app
   * name, the icon, the splash or the adaptive icon (release lines U8, KTD4).
   * The payload names the field and the lane, never the value; the draft row
   * holds the value and who saved it, and keeps only the latest, so this row is
   * the history of who proposed what change to a school's binary, and when.
   *
   * Operations catalog only, like `app.store_listing_updated`: the mobile-app
   * catalog is frozen.
   */
  'app.field_draft_saved': 'configuration',
  /**
   * A school asked for its pending drafts to be released (release lines U9,
   * R14, KTD6), and whether the post to #app-operations landed. Deduped on the
   * app and its pending fields with their saved times, so a second request
   * with no new save is the same request; a new save makes a new one.
   *
   * `delivery`, like the rejection pair: the request asks staff to ship, and
   * the post outcome is the evidence it reached them. A plain save never
   * notifies; only this does. Operations catalog only (frozen mobile-app
   * catalog).
   */
  'app.release_requested': 'delivery',
  'app.release_request_posted': 'delivery',
  /**
   * Staff released an app on a release line (release lines U13, R22, KTD9):
   * pushed its store content, or applied its build drafts and rebuilt, or
   * shipped an OTA, per platform. The payload carries each platform's line,
   * status and step outcomes; the app × line record's shipped snapshot keeps
   * only the latest, so this row is the history of every release attempt,
   * partial and failed ones included.
   *
   * `delivery`, like `build.dispatched`. Operations catalog only (frozen
   * mobile-app catalog).
   */
  'app.released_on_line': 'delivery',
  /**
   * Staff asked for a store drift check on one app, line and platform (release
   * lines U15, R24): the live store listing was read and compared with the
   * app × line record's shipped snapshot. The record keeps only the latest
   * result; this row says who asked and what the answer was at the time.
   * Checks the fleet delivery sweep makes record nothing here.
   *
   * `delivery`, like `app.released_on_line`. Operations catalog only (frozen
   * mobile-app catalog).
   */
  'app.release_line_drift_checked': 'delivery',
  /**
   * The backend's `mobile/v<version>` tag writer (SCH-2563 KTD1, R4): the tag
   * was created, already existed at another commit, or could not be written.
   * Subject is the release whose commit the tag names, so the outcome shows on
   * that release's page. Tagging never fails a release, so these rows are the
   * only place a missing or disputed tag surfaces. Payload shapes live in
   * `mobile-version-tag.ts`.
   *
   * `delivery`, like `app.released_on_line`. Operations catalog only (frozen
   * mobile-app catalog).
   */
  'version.tagged': 'delivery',
  'version.tag_conflict': 'delivery',
  'version.tag_failed': 'delivery',
  /**
   * An operator turned App Store / Play demo login on or off for one platform
   * of one app, by adding or removing that app's namespace from the shared
   * `app_store_demo_login` PostHog flag.
   *
   * The only record that the change happened: the flag itself stores no
   * attribution, and the platform stores no mirror of its state. One type for
   * both directions, with the direction in the payload — the question this
   * answers is "who last touched demo login for this app", and splitting it in
   * two would make that a union of two queries for no gain.
   */
  'app.demo_login_changed': 'configuration',
  'app.version_created': 'configuration',
  /**
   * The staff calendar fleet sweep replaced a school's borrowed-calendar pick
   * because the previous one stopped working (SCH-1942, KTD6).
   *
   * `domain: 'integration'`, `subject: null` — the event is about a school's
   * Blackbaud wiring, and `school` is not an `entityType`. Operations catalog
   * only; the mobile-app catalog is frozen.
   *
   * Recorded on the TRANSITION, not on every run: a school that has been
   * replaced-and-stable for a week should not emit a row a day. The payload is
   * the school's PUBLIC id, the outcome and the reason, and nothing else — the
   * lane borrows a named person's Blackbaud token, and this is a browsable
   * surface.
   */
  'staff_calendar.pick_replaced': 'configuration',
  /**
   * The sweep found no candidate at a school that Blackbaud would serve a
   * calendar to (SCH-1942, R15).
   *
   * The school-side ask: somebody there has to connect Blackbaud before staff
   * can see a calendar, and nothing else in the platform would say so. Same
   * PII-free payload as `staff_calendar.pick_replaced`, and recorded on the
   * transition for the same reason. `probe-budget-exhausted` deliberately does
   * NOT emit: it means the run ran out of probes, not that the school is stuck.
   */
  'staff_calendar.none_available': 'configuration',

  // --- asset: image slots (the history that exists nowhere else) ---
  'asset.slots_initialized': 'asset',
  'asset.committed': 'asset',
  /**
   * Store screenshots (SCH-1945). Carried in both catalogs, like every other
   * `asset` type: a screenshot write goes through `recordOperationsEvent`, whose
   * type parameter is this union, and the entry-for-entry test below holds the
   * two in step.
   *
   * A screenshot is not a slot. It belongs to an ordered set keyed by store,
   * locale, version and device class, so the payload is
   * `{ store, versionString, deviceClass, position?, cloudinaryPublicId?,
   * fromVersion?, toVersion?, copied?, removed?, added?, source }`. `source`
   * names the lane — `admin` for the super-admin panel, `internal` for the
   * API-key lane a script or an AI workflow writes through, which is the only
   * attribution that lane has.
   */
  'screenshot.committed': 'asset',
  'screenshot.removed': 'asset',
  'screenshot.set_copied': 'asset',
  'screenshot.set_replaced': 'asset',

  // --- onboarding: the provisioning orchestrator ---
  'provisioning.run_started': 'onboarding',
  /**
   * The four `provisioning.step_*` telemetry types — `step_succeeded`,
   * `step_failed`, `step_retried`, and `browser_dispatch_step` below — STAY on
   * the timeline for now.
   *
   * The plan retires them into `AutomationStep` rows, but `AutomationStep` is
   * promotion 2 (KD11). Dropping them in promotion 1 would strand four live
   * write paths with nowhere to write. They move when the table they move into
   * exists, which is exactly what KD11's split implies.
   */
  'provisioning.step_succeeded': 'onboarding',
  'provisioning.step_failed': 'onboarding',
  'provisioning.step_retried': 'onboarding',
  /**
   * A human operator overrode a stuck step. Unlike the four above this one
   * stays on the timeline PERMANENTLY: it records a decision, not machine
   * telemetry, and `AutomationStep` has no actor columns at all and reaps at
   * 30 days — moving it would destroy the attributability R8 requires.
   */
  'provisioning.step_manually_completed': 'onboarding',
  'provisioning.run_completed': 'onboarding',
  'provisioning.run_failed': 'onboarding',
  // Browser-dispatch lifecycle: a batch of portal steps handed to a Journey CLI
  // runner (a detached local child process), whose per-step outcomes come back
  // through the internal report endpoint.
  'provisioning.browser_dispatch_created': 'onboarding',
  /** Per-step telemetry — moves to `AutomationStep` in promotion 2, see above. */
  'provisioning.browser_dispatch_step': 'onboarding',
  'provisioning.browser_dispatch_finished': 'onboarding',

  // --- push: OneSignal wiring ---
  'push.config_updated': 'push',

  // ===========================================================================
  // FLEET FIREBASE — the inventory, adoption and approval lane (SCH-2134 U4a;
  // plan 2026-09-21-1241, KTD3).
  //
  // Four types, and NO inventory-run table: the plan's first revision gave the
  // fleet its own ledger, and this spine replaced it. An inventory pass is a
  // run-summary row plus one decision row per label, and the binding's
  // `allocationEvidencePublicId` points back at the decision row that
  // authorized it. `entityType: mobile_app` for anything about a label; the run
  // summary is `subject: null`, because a pass is about the fleet.
  // ===========================================================================
  /**
   * The inventory classified one label: `adopt`, `move_then_adopt`, `create` or
   * `conflict`, with the evidence that produced it and the run's fingerprint.
   *
   * `lifecycle`, not `configuration`: this is where a Firebase binding's state
   * comes from, and the filter an operator reaches for after a pass is "what
   * did the fleet decide about these apps", not "what settings changed".
   *
   * Deduped on `[fingerprint, label, decision]`, so re-running over unchanged
   * facts writes nothing new — which is what makes the script safe to run as
   * often as an operator wants before approving anything.
   */
  'fleet.firebase_inventory_classified': 'lifecycle',
  /** One row per pass: principal, coverage, the fingerprint and the decision counts. */
  'fleet.firebase_inventory_completed': 'lifecycle',
  /**
   * An operator's New Project console observation: how many projects the
   * organization may still create, and when they looked (KTD3, R21).
   *
   * `compliance`, beside `account.agreement_checked` — it is a human attesting
   * to an external condition the platform cannot read for itself, and it is the
   * row an auditor asks for by name when a cohort of creates is questioned.
   */
  'fleet.firebase_quota_attested': 'compliance',
  /**
   * An operator approved one binding's allocation decision AGAINST A NAMED
   * FINGERPRINT.
   *
   * The fingerprint is the whole point: an approval authorizes a decision made
   * from a specific set of facts, so a later inventory that observes anything
   * different invalidates it rather than carrying it forward (KTD3). Recorded
   * even when the approval is refused for a mismatch, because "somebody tried
   * to approve stale evidence" is exactly the thing that must not be silent.
   */
  'fleet.firebase_allocation_approved': 'compliance',
  /**
   * One approved `move_then_adopt` project was moved into the fleet folder and
   * its before/after snapshots compared (SCH-2192 U4b, KTD12). One row per
   * outcome — `verified` or `mismatch` — carrying the project ID and number,
   * the from/to parent, the Analyze Move summary and the field-level diff.
   *
   * `lifecycle`, beside the inventory types: a move is where a binding's
   * project changes parent, and it does NOT activate the binding — that waits
   * for a push proof and the IAM convergence step.
   */
  'fleet.firebase_project_moved': 'lifecycle',
  /**
   * Staff signed off that a release line's compliance answers for one platform
   * and field were applied, and by which lane (release lines U14, R2, KTD11).
   * `subject: null`: a line is fleet-wide, not any one app's. The sign-off row
   * keeps only the latest answer, so this row is the history of who signed what,
   * when, and what it replaced (a carried or revoked sign-off).
   *
   * `compliance`, beside `fleet.firebase_quota_attested`: a human attesting to
   * a store-side condition the platform cannot read for itself.
   */
  'release_line.compliance_signed_off': 'compliance',
  /**
   * Staff revoked a line's compliance sign-off, usually a carried one whose
   * answer changed on the new line (release lines U14, R23). Submissions on the
   * line are refused again until the field is signed off. `subject: null`.
   */
  'release_line.compliance_revoked': 'compliance',

  // ===========================================================================
  // New in this spine — publishing accounts (`entityType: publishing_account`)
  //
  // These entities record NOTHING today. Their state is a set of last-write
  // columns, so "who changed the ownership answer, and when" is unanswerable
  // and a failed agreement check is erased by the next passing one.
  // ===========================================================================
  /**
   * We started tracking this account — NOT that anyone enrolled it with the
   * vendor.
   *
   * NOT `account.enrolled`, and do not rename it back. Enrolling with Apple is a
   * portal-only, payment-bearing act by a human that this platform cannot
   * perform, and "enrolled" already means Apple's OWN enrollment kind in the
   * data next door: `PublishingAccountObservedStanding.enrolledAs` holds the
   * Membership Details field of that name (Organization / Individual). The
   * operator surface generates its label mechanically from this string, so the
   * event type IS the user-facing text — "Account enrolled" beside "Enrolled as:
   * Organization" reads as a claim we made an account at Apple.
   */
  'account.registered': 'lifecycle',
  /** Payload carries from → to: the attribution that does not exist today. */
  'account.ownership_answered': 'lifecycle',
  'account.kind_answered': 'lifecycle',
  'account.contact_changed': 'lifecycle',
  'account.display_name_changed': 'lifecycle',
  /**
   * How far the account has got with the vendor MOVED (credential-home R21).
   *
   * `lifecycle`, not `credential`: the state describes the ACCOUNT's standing
   * with the vendor, and `api_ready` is reached by observing a successful call
   * rather than by anything happening to a key. Payload carries from → to.
   */
  'account.provisioning_state_changed': 'lifecycle',
  /**
   * NO WRITER IN PROMOTION 1 — its write path is the U44 standing sweep.
   *
   * Emitted ONLY when the observed standing DIFFERS from the last recorded
   * value. A weekly sweep writing an identical row every run would bury the one
   * refusal that matters under thousands of no-change confirmations; unchanged
   * confirmations belong in `AutomationRun`/`AutomationStep`, which reap at 30
   * days.
   *
   * Payloads carry the standing record's public id plus non-PII fields only —
   * the operator surface joins to that row for the holder's name and email, so
   * R10 stays absolute and the recorder needs no per-field exemption.
   */
  'account.standing_observed': 'lifecycle',
  /**
   * NO WRITER IN PROMOTION 1 — its write path is the U44 standing sweep.
   *
   * Written on EVERY failed check: a check that did not run must never clear a
   * finding.
   */
  'account.standing_check_failed': 'lifecycle',
  /** Reason required — the lever that ships today has no reason field at all. */
  'account.refused': 'compliance',
  /** Reason required. */
  'account.unrefused': 'compliance',
  /**
   * LEGACY — superseded by `account.api_access_checked` below.
   *
   * KEPT IN THE UNION, deliberately, until the 24-month reaper has aged out the
   * rows already written under it (2028). Deleting it would make every one of
   * those rows unclassifiable by `operationsEventClassFor`, and the timeline
   * derives its label from the type string, so they would render as nothing.
   * Nothing writes it any more.
   */
  'account.agreement_checked': 'compliance',
  /**
   * ONE API-ACCESS ATTEMPT HAPPENED, with the outcome in the payload (R9, KTD5).
   *
   * Written on EVERY attempt by the operator re-check, with NO `dedupeOn`: two
   * checks minutes apart are two facts about the account, and a stable key would
   * make the second a successful no-op — silently reinstating the erasure this
   * instrumentation removes. The reconcile writes it only for a `check_failed`
   * outcome, deduped on a fixed-arity day-bucketed key, because a nightly sweep
   * recording every pass would bury the one refusal that matters.
   *
   * It replaces `account.agreement_checked` because the axis it reports is no
   * longer Apple's developer agreement: Play refuses a read for an unlinked
   * project or a missing permission, and either vendor refuses a key it will not
   * accept. The timeline derives its label from the type string, so one type
   * could not honestly carry both meanings.
   */
  'account.api_access_checked': 'compliance',
  /**
   * THE API-ACCESS STATE MOVED — the reconcile's transition type.
   *
   * NO WRITER IN THIS PROMOTION; its write path is the scheduled reconcile.
   * Separate from the attempt type above because a transition and an attempt are
   * different questions, and `dedupeOn` is fixed-arity —
   * `[utcDay, fromState, toState, reasonOrNone]`, with `'none'` as the sentinel
   * because `dedupeOn` admits no null and a variable-arity key can collide
   * across different meanings.
   */
  'account.api_access_changed': 'compliance',
  /**
   * NO WRITER IN PROMOTION 1 — its write path is the credential vault.
   *
   * Recorded against the ACCOUNT. The current event fires against one app while
   * credentials are account-scoped, so a shared team's credential history is
   * smeared across eight app timelines.
   */
  'account.credentials_imported': 'credential',
  /** NO WRITER IN PROMOTION 1 — its write path is the credential vault. */
  'account.credentials_revoked': 'credential',

  // ===========================================================================
  // New in this spine — webhook plumbing (`domain: infrastructure`, subject NULL)
  // ===========================================================================
  /**
   * A delivery verified its signature and then resolved to no entity we hold —
   * currently invisible, and the shape of a silent integration break.
   *
   * `webhook.signature_rejected` is deliberately ABSENT and must not be added.
   * A rejection requires no secret, so recording one would let any
   * unauthenticated caller write unlimited rows into a 24-month table by POSTing
   * garbage at a public path. Rejections stay on the existing per-handler
   * 401 + `logger.warn`, with a Datadog monitor on that log line.
   */
  'webhook.received_unroutable': 'lifecycle',

  // ===========================================================================
  // Browser-session renewal (SCH-1813 U2), `domain: 'integration'`, `subject:
  // null` — a renewal is addressed by `correlationId = renewal_<id>`, the
  // `BrowserSessionRenewal` row, not by one of the three entity types.
  //
  // PAYLOADS CARRY CODES, NEVER FREE TEXT (KTD16). These reach Slack through
  // `services/operations-events/notify.ts`, so a runner-supplied string here
  // would be a path for a cookie value, a password or a 2FA code to leave the
  // process (R11). The backend writes every field from a closed list.
  // ===========================================================================
  /** A sign-in succeeded and the new session was saved through the compare-and-set. */
  'browser_session.renewed': 'credential',
  /** A renewal ended FAILED (non-halting outcome) or REFUSED `scheduled-too-soon`. */
  'browser_session.renewal_failed': 'credential',
  /** A halting outcome (R9) stopped every automatic renewal until a super-admin resumes. */
  'browser_session.renewal_halted': 'credential',
  /** A super-admin cleared the halt. */
  'browser_session.renewal_resumed': 'credential',
  /**
   * A super-admin asked for a renewal from the console (SCH-1813 U5).
   *
   * Recorded at the REQUEST, not at the claim, because this is the moment a
   * PERSON decided the platform should submit the shared Apple ID password —
   * and the claim that follows is attributed to the renewal process, not to
   * them. Without this, a request that the ledger then refuses (cooldown, cap,
   * a lane someone else halted) leaves the operations timeline with no trace
   * that anyone asked.
   */
  'browser_session.renewal_requested': 'credential',
  /**
   * A super-admin typed the 2FA code into the console (SCH-1813 U5).
   *
   * The payload carries WHO typed it and never the digits — the code reaches
   * Slack through the notifier like every other payload here (R11), and the
   * code store is the only place it is allowed to live.
   */
  'browser_session.code_submitted': 'credential',
  /** The runner is waiting for a code a person may have to type in the console. */
  'browser_session.renewal_awaiting_code': 'credential',
  /** A six-digit text arrived that could not be attributed to this renewal. Never halts. */
  'browser_session.code_incident': 'credential',
  /**
   * The verification-code relay saw an Apple-format code from a sender that is
   * NOT in the `apple_session_renewal` flag's `appleSenders`, and did not serve
   * it (2026-10-09: Apple began texting from a new number and every renewal
   * timed out). Recorded by the relay, not the runner: one row per sender per
   * sign-in window, `correlationId = apple_sender_<sender>`. The payload names
   * the sender and never the code or the body.
   */
  'browser_session.unpinned_apple_sender': 'credential',

  // ===========================================================================
  // New in this spine — the agent run ledger (SCH-1186 U2), `domain: 'agent'`,
  // `subject: null` (a run is addressed purely by `correlationId = run_<publicId>`,
  // not by one of the three `OperationsEventEntityType` values).
  // ===========================================================================
  'impersonation.actor_token_created': 'credential',
  /**
   * One read through the investigate Blackbaud broker (`GET /internal/investigate/blackbaud`,
   * SCH-2515 U3), accepted or refused. Recorded per CALL, carrying the investigate run id, the
   * school, the persona, the product, the path and the verdict.
   *
   * `credential` for the reason `impersonation.actor_token_created` is: an accepted read mints
   * a Blackbaud token and acts as that persona. `subject: null` like the rest of this block —
   * the run is named in the payload, not by an entity pointer.
   */
  'investigate.blackbaud_read': 'credential',
  'run.started': 'agent_run',
  'run.gate1': 'agent_run',
  'run.gate2': 'agent_run',
  'run.parked': 'agent_run',
  'run.resumed': 'agent_run',
  'run.blocked': 'agent_run',
  'run.done': 'agent_run',
  'run.failed': 'agent_run',
  'run.retro': 'agent_run',
  'run.note': 'agent_run',

  // ===========================================================================
  // The run's OUTCOMES — `AGENT_RUN_OUTCOME_EVENT_TYPES` (agent-run.ts), one
  // row per concrete thing a run produced. Same `domain`/`subject`/
  // `correlationId` shape as the ten lifecycle types above; the difference is
  // that these never move `AgentRun.status` and never touch its `summary`.
  //
  // They are `agent_run` rather than a new class on purpose: the class is what
  // the timeline UI filters on, and every one of these belongs on the run's own
  // trail beside the lifecycle events that bracket it. A separate class would
  // split one run's history across two filters.
  // ===========================================================================
  'run.findings.extracted': 'agent_run',
  'run.school.resolved': 'agent_run',
  'run.school.unresolved': 'agent_run',
  'run.linear.issue_created': 'agent_run',
  'run.linear.comment_attached': 'agent_run',
  'run.linear.need_created': 'agent_run',
  'run.linear.write_failed': 'agent_run',
  'run.hubspot.meeting_created': 'agent_run',
  'run.hubspot.meeting_skipped': 'agent_run',
  'run.hubspot.meeting_failed': 'agent_run',
  'run.dispatch.intent_recorded': 'agent_run',
  // The factory's classifier verdicts and the phase runner's step edges
  // (phase runner plan, U3). Same class, same trail.
  'run.classified': 'agent_run',
  'run.phase': 'agent_run',
  // KLEOS was told what happened to a triage run's Linear issue (customer
  // issue loop U7, KTD8): one row per accepted `issue.resolved` delivery,
  // correlated by the run id. The latest row is the run's notified state, so
  // the watcher never writes the terminal run row. Deliberately NOT a
  // `run.<type>` outcome: those are reportable over the run-token events
  // route, and a run must not be able to forge "KLEOS was already told".
  'kleos.issue_resolved_sent': 'agent_run'
} as const satisfies Record<string, OperationsEventClass>

export type OperationsEventType = keyof typeof OPERATIONS_EVENT_TYPES

export const OPERATIONS_EVENT_TYPE_LIST = Object.keys(OPERATIONS_EVENT_TYPES) as OperationsEventType[]

export const OperationsEventTypeSchema = z.enum(
  OPERATIONS_EVENT_TYPE_LIST as [OperationsEventType, ...OperationsEventType[]]
)

/**
 * `Object.hasOwn`, not `in` — `in` walks the prototype chain, so the `in`-based
 * guard this catalog inherited answers `true` for `'toString'` and
 * `'constructor'`. A recorder that trusts it would then derive an eventClass of
 * `[Function]` and write a row no filter can find.
 */
export function isOperationsEventType(value: string): value is OperationsEventType {
  return Object.hasOwn(OPERATIONS_EVENT_TYPES, value)
}

/** The class an event type belongs to. Never let a caller supply this itself. */
export function operationsEventClassFor(eventType: OperationsEventType): OperationsEventClass {
  return OPERATIONS_EVENT_TYPES[eventType]
}

// =============================================================================
// Entity (wire shape)
// =============================================================================

/**
 * The read surface. PUBLIC ids only, per the repo's response convention:
 * accepting or emitting the internal autoincrement would be an enumerable
 * direct-object reference across every operational entity in the fleet.
 *
 * The three rollup keys (`mobileAppId`, `publishingAccountId`, `appReleaseId`)
 * are deliberately NOT on the wire. They are internal `BigInt` FKs and query
 * keys, not display fields; serializing them would cost three joins to produce
 * public ids that nothing renders. The reader accepts them as FILTERS addressed
 * by public id and resolves them server-side.
 */
export const OperationsEventSchema = z.object({
  /** OperationsEvent.publicId. */
  id: z.string(),
  domain: OperationsEventDomainSchema,
  /** Null when the event is about the platform itself rather than an entity. */
  entityType: OperationsEventEntityTypeSchema.nullable(),
  /** The subject's PUBLIC id, denormalized on the row so it reads even if the subject is gone. */
  entityId: z.string().nullable(),
  eventClass: OperationsEventClassSchema,
  eventType: OperationsEventTypeSchema,
  severity: OperationsEventSeveritySchema,
  actorType: OperationsEventActorTypeSchema,
  actorId: z.string().nullable(),
  actorLabel: z.string().nullable(),
  approverId: z.string().nullable(),
  summary: z.string().nullable(),
  payload: z.record(z.string(), z.unknown()).nullable(),
  /** Joins a timeline event to the run that produced it. */
  correlationId: z.string().nullable(),
  /**
   * When it HAPPENED, which is not when we heard about it — webhooks arrive
   * late, and a single timestamp makes ordering lie.
   */
  occurredAt: z.string().datetime(),
  createdAt: z.string().datetime()
})
export type OperationsEvent = z.infer<typeof OperationsEventSchema>

// =============================================================================
// List contract
// =============================================================================

// `OperationsEventListSchema` lived here from #2779 and was removed 2026-08-23,
// when the reader began returning a coverage verdict alongside the page. It had
// exactly one consumer — the service's return type — and no wire contract ever
// composed it: `ListOperationsEventsResponseSchema` builds on
// `OperationsEventSchema` directly. The service's page shape is internal, not a
// published contract, so it lives with the service as `OperationsEventPage`.
// Kept as a note rather than silently deleted because an intermediate contract
// disappearing from a shared package reads as an accident otherwise.

export const OPERATIONS_EVENT_PAGE_SIZE_DEFAULT = 50
export const OPERATIONS_EVENT_PAGE_SIZE_MAX = 200

// =============================================================================
// Rejection remedy run payloads (SCH-2889)
// =============================================================================

/**
 * What `app.rejection_remedy_started` carries.
 *
 * `runId` is the setup-drive dispatch id for a graph template, or the prompt
 * job's AI execution id for a prompt template; the matching
 * `app.rejection_remedy_ended` names the same id. `coveredCorrelationIds` is
 * every open rejection of the same app, store and version the run answers
 * (R12). A prompt template records the target field's value before and after
 * its write; a graph template leaves both out. No reviewer text (R8).
 */
export const RejectionRemedyStartedPayloadSchema = z.object({
  runId: z.string().min(1),
  templateId: StoreRejectionTemplateIdSchema,
  coveredCorrelationIds: z.array(z.string().min(1)).min(1),
  store: StoreRejectionStoreSchema,
  targetPreviousValue: z.string().nullable().optional(),
  targetWrittenValue: z.string().nullable().optional(),
  /** Set only on a prompt run's drive event: the setup drive it started. A graph run's drive is its run id. */
  driveId: z.string().min(1).optional()
})
export type RejectionRemedyStartedPayload = z.infer<typeof RejectionRemedyStartedPayloadSchema>

/**
 * What `app.rejection_remedy_ended` carries. `status` is always terminal.
 * `stopReason` names the step where a run stopped short of `resubmitted` (R14).
 */
export const RejectionRemedyEndedPayloadSchema = z.object({
  runId: z.string().min(1),
  status: StoreRejectionRemedyRunEndStatusSchema,
  stopReason: z.string().nullable().optional()
})
export type RejectionRemedyEndedPayload = z.infer<typeof RejectionRemedyEndedPayloadSchema>
